import React from "react";
import { useNavigate } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-6 pt-28 pb-16">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center cursor-pointer" onClick={() => navigate("/")}>
              <Shield className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold cursor-pointer" onClick={() => navigate("/")}>JepTags</span>
          </div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-blue-200 hover:text-white mb-4 text-sm transition">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <h1 className="text-4xl font-bold mb-3">Terms of Service</h1>
          <p className="text-blue-100">Last updated: February 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 md:p-12 shadow-sm space-y-8">

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              By accessing or using JepTags' product authentication platform, website, mobile applications, APIs, and related services (collectively, the "Services"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Services</h2>
            <p className="text-gray-600 leading-relaxed">
              JepTags provides a product authentication and traceability platform that enables businesses to protect their products using NFC, RFID, and QR code technology. Services include tag provisioning and management, consumer-facing product verification, real-time analytics and reporting dashboards, lottery and consumer engagement tools, API access for system integration, and supply chain traceability features.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Account Registration</h2>
            <p className="text-gray-600 leading-relaxed">
              To access business features, you must register for an account. You agree to provide accurate and complete information, maintain the security of your account credentials, notify us immediately of any unauthorized access, and accept responsibility for all activities under your account. We reserve the right to suspend or terminate accounts that violate these terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Subscription & Payments</h2>
            <p className="text-gray-600 leading-relaxed">
              Paid features are available through subscription plans (Starter, Professional, Enterprise). Subscriptions are billed monthly or annually as selected. You authorize us to charge your payment method on file. Prices may change with 30 days' notice. Refunds are handled on a case-by-case basis. Free trials do not require payment information unless specified.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Acceptable Use</h2>
            <p className="text-gray-600 leading-relaxed">
              You agree not to use the Services to violate any applicable laws or regulations, infringe on intellectual property rights, distribute malware or harmful code, attempt to gain unauthorized access to our systems, use the platform to authenticate counterfeit or illegal goods, or resell or redistribute the Services without authorization.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
            <p className="text-gray-600 leading-relaxed">
              All content, features, and functionality of the Services — including software, text, graphics, logos, and APIs — are owned by JepTags and protected by intellectual property laws. You retain ownership of your product data and content uploaded to the platform. By using the Services, you grant us a limited license to process and display your content as necessary to provide the Services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data & Privacy</h2>
            <p className="text-gray-600 leading-relaxed">
              Your use of the Services is also governed by our <span className="text-blue-600 cursor-pointer hover:underline" onClick={() => navigate('/privacy')}>Privacy Policy</span>. You are responsible for ensuring that your use of scan data and consumer information complies with applicable data protection laws in your jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-600 leading-relaxed">
              To the maximum extent permitted by law, JepTags shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Services. Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim. The Services are provided "as is" without warranties of any kind, express or implied.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Termination</h2>
            <p className="text-gray-600 leading-relaxed">
              Either party may terminate the agreement at any time. We may suspend or terminate your access immediately if you violate these Terms. Upon termination, your right to use the Services ceases. We will retain your data for 90 days after termination, after which it may be permanently deleted.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms are governed by the laws of the State of California, United States, without regard to conflict of law provisions. Any disputes shall be resolved in the courts of Santa Clara County, California.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact</h2>
            <p className="text-gray-600 leading-relaxed">
              For questions about these Terms of Service, contact us at:
            </p>
            <div className="bg-blue-50 rounded-lg p-4 mt-3">
              <p className="text-gray-800 font-medium">JepTags, Inc.</p>
              <p className="text-gray-600 text-sm">2635 Cunningham Ave, California, CA 95148</p>
              <p className="text-gray-600 text-sm">Email: legal@jeptags.com</p>
              <p className="text-gray-600 text-sm">Phone: +1-844-737-4799</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
